
import { useState, useEffect, useCallback, useRef } from 'react';
import { APIEndpoint } from '../utils/axios';
import { storageUtil } from '../utils/commonFunctions';

const DISMISS_DURATION_MS_ENV = Number(process.env.REACT_APP_ACCOUNT_DISMISS_WARNING_TIME);
const DISMISS_DURATION_MS = Number.isFinite(DISMISS_DURATION_MS_ENV) ? DISMISS_DURATION_MS_ENV : 24 * 60 * 60 * 1000; // 24 hours recheck
const POLL_INTERVAL_MS_ENV = Number(process.env.REACT_APP_ACCOUNT_LOGOUT_IDLE_TIME);
const POLL_INTERVAL_MS = Number.isFinite(POLL_INTERVAL_MS_ENV) ? POLL_INTERVAL_MS_ENV : 60 * 60 * 1000;


export function usePaymentWarning(sessionId) {
  const [warning, setWarning] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const controllerRef = useRef(null);

  // Fetch stepAPI 
  const fetchPaymentInfo = useCallback(async (signal) => {
    if (!sessionId) return;
    
    try {
      const resp = await APIEndpoint.post(
        'customer/step-info',
        { id: sessionId, process: 'profile' },
        { signal }
      );

      const { paymentOverdue } = resp ?? {};

      if (paymentOverdue?.enabled) {
        setWarning(paymentOverdue.message ?? null);
        setDueDate(paymentOverdue.dueDate ?? null);
      } else {
        setWarning(null);
        setDueDate(null);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('[usePaymentWarning] fetch failed:', err);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setWarning(null);
      setDueDate(null);
      setIsDismissed(false);
      return;
    }

    controllerRef.current = new AbortController();
    const dismissedKey = `notification_dismissed_${sessionId}`;
    const dismissedTime = storageUtil.get(dismissedKey, 'local');

    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime);
      const isExpired = timeSinceDismissed > DISMISS_DURATION_MS;

      if (!isExpired) {
        setIsDismissed(true);
        return;
      } else {
        storageUtil.clear(dismissedKey, 'local');
      }
    }


    setIsDismissed(false);
    fetchPaymentInfo(controllerRef.current.signal);

    const pollTimer = setInterval(() => {
      const dismissedTime = storageUtil.get(dismissedKey, 'local');     
      if (!dismissedTime) {    
        return;
      }

      const dismissedAt = Number(dismissedTime);
      const timeSinceDismissed = Number.isFinite(dismissedAt) ? Date.now() - dismissedAt : Infinity;
      const isExpired = timeSinceDismissed > DISMISS_DURATION_MS;

      if (isExpired) {
        storageUtil.clear(dismissedKey, 'local');
        setIsDismissed(false);
        fetchPaymentInfo(controllerRef.current.signal);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollTimer);
      controllerRef.current?.abort();
    };
  }, [sessionId, fetchPaymentInfo]);

  const dismissWarning = useCallback(() => {
    const dismissedKey = `notification_dismissed_${sessionId}`;
    storageUtil.set(dismissedKey, Date.now().toString(), 'local');
    setIsDismissed(true);
  }, [sessionId]);

  return {
    warning,
    dueDate,
    dismissWarning,
    isDismissed,
  };
}
